import { useState, useEffect, useRef } from 'react';
import { txApi } from './txApi.js';
import OrbitModuleHeader from '../../shared/hub/OrbitModuleHeader.jsx';
import { useWorkspace } from '../../shared/workspace/WorkspaceContext.jsx';

import PlatformsPage      from './pages/PlatformsPage.jsx';
import ClientsPage        from './pages/ClientsPage.jsx';
import TeamAccessPage     from './pages/TeamAccessPage.jsx';
import TaxonomySetup      from './pages/TaxonomySetup.jsx';
import UtmSetup from './pages/UtmSetup.jsx';
import CampaignsPage      from './pages/CampaignsPage.jsx';
import ActivityPage       from './pages/ActivityPage.jsx';
import DataDictionaryPage from './pages/DataDictionaryPage.jsx';
import BuilderPage        from './pages/BuilderPage.jsx';
import VersionPreviewPage from './pages/VersionPreviewPage.jsx';

// The module's palette lives in pages/_shared.jsx — this file used to keep a
// duplicate copy, which is how the two drifted apart.
import { C as T } from './pages/_shared.jsx';

const BASE = '/taxonomy-generator';

const SETTINGS_TABS = [
  { id: 'platforms', label: 'Platforms' },
  { id: 'clients',   label: 'Clients' },
  { id: 'users',     label: 'Users & Access' },
];

const MAIN_NAV = [
  { id: 'campaigns', label: 'Campaigns',    path: 'campaigns' },
  { id: 'activity',  label: 'Activity Log', path: 'activity-log' },
];
const TAXONOMY_NAV = [
  { id: 'utm', label: 'UTM Setup', path: 'utm-setup' },
  { id: 'taxonomy',   label: 'Taxonomy Setup',  path: 'taxonomy-setup' },
  { id: 'dictionary', label: 'Data Dictionary', path: 'data-dictionary' },
];
const SUPER_ADMIN_NAV = [
  { id: 'settings', label: 'Settings', path: 'settings' },
];

function navigate(path) {
  history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function parsePath() {
  const p = window.location.pathname;
  const rest = p.startsWith(BASE) ? p.slice(BASE.length) : '/';
  const empty = rest === '' || rest === '/';

  const vm = rest.match(/^\/campaigns\/([^/]+)\/version\/([^/]+)$/);
  if (vm) return { view: 'campaign-version', campaignId: vm[1], versionId: vm[2] };

  // A dedicated two-segment suffix cannot collide with a user-defined taxonomy level.
  const utm = rest.match(/^\/campaigns\/([^/]+)\/utm\/links$/);
  if (utm) return { view: utm[1] === 'new' ? 'builder-new' : 'campaign-detail', campaignId: utm[1], utm: true };

  const m = rest.match(/^\/campaigns\/([^/]+)(?:\/([^/]*))?$/);
  if (m) {
    const [, id, lvl] = m;
    if (id === 'new') return { view: 'builder-new', level: lvl || null };
    return { view: 'campaign-detail', campaignId: id, level: lvl || null };
  }

  if (empty || rest.startsWith('/campaigns')) return { view: 'campaigns' };
  if (rest === '/activity-log')    return { view: 'activity' };
  if (rest === '/taxonomy-setup')  return { view: 'taxonomy' };
  if (rest === '/utm-setup') return { view: 'utm' };
  if (rest === '/data-dictionary') return { view: 'dictionary' };
  if (rest.startsWith('/settings')) {
    const tab = rest.slice('/settings'.length).replace(/^\//, '') || 'platforms';
    return { view: 'settings', settingsTab: tab };
  }
  return { view: 'campaigns' };
}

export default function TaxonomyApp() {
  const { clientId, clients, setClientId, loading: workspaceLoading } = useWorkspace();
  const previousClient = useRef(clientId);
  const [route,         setRoute]        = useState(parsePath);
  const [me,            setMe]           = useState(null);
  const [loading,       setLoading]      = useState(true);
  const [error,         setError]        = useState(null);
  const builderPropsRef = useRef({});
  const [campaignCache, setCampaignCache] = useState(null); // { id, props } | { id, error: true }
  const [campLoading,   setCampLoading]  = useState(false);

  useEffect(() => {
    txApi.getMe()
      .then(d => { setMe(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => {
    const handler = () => setRoute(parsePath());
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // An explicit workspace change leaves any client-bound editor or version preview.
  useEffect(() => {
    const changed = previousClient.current && previousClient.current !== clientId;
    previousClient.current = clientId;
    if (changed && ['campaign-detail', 'campaign-version', 'builder-new'].includes(route.view)) {
      setCampaignCache(null);
      builderPropsRef.current = {};
      navigate(`${BASE}/campaigns`);
    }
  }, [clientId, route.view]);

  // Clear cache whenever leaving campaign-detail so re-entry always fetches fresh data
  useEffect(() => {
    if (!['campaign-detail', 'campaign-version'].includes(route.view)) setCampaignCache(null);
  }, [route.view]);

  // Fetch campaign group when entering campaign-detail view
  useEffect(() => {
    const { view, campaignId } = route;
    if (!['campaign-detail', 'campaign-version'].includes(view) || !campaignId || workspaceLoading) return;
    if (campaignCache?.id === campaignId) return;

    let alive = true;
    setCampLoading(true);
    txApi.getCampaignGroup(campaignId)
      .then(d => {
        if (!alive) return;
        const gd = d.group || {};
        // A direct campaign link selects its permitted workspace before mounting the editor.
        if (String(gd.client_id) !== String(clientId)) {
          if (!clients.some(c => String(c.id) === String(gd.client_id))) {
            setCampaignCache({ id: campaignId, error: true });
            return;
          }
          previousClient.current = String(gd.client_id);
          setClientId(gd.client_id);
        }
        const rowsByLevel = {};
        const sortedCampaigns = [...(gd.campaigns || [])].sort((a, b) => {
          const da = a.display_order, db = b.display_order;
          if (da != null && db != null) return Number(da) - Number(db);
          return Number(a.id) - Number(b.id);
        });
        sortedCampaigns
          .filter(c => String(c.platform_id) === String(gd.platform_id))
          .forEach(c => {
            const lvlName = c.level || '';
            const fv = { ...(typeof c.field_values === 'string' ? JSON.parse(c.field_values) : (c.field_values || {})) };
            if (!rowsByLevel[lvlName]) rowsByLevel[lvlName] = [];
            rowsByLevel[lvlName].push({ id: String(c.id), values: fv });
          });
        setCampaignCache({
          id: campaignId,
          props: {
            initialClientId:     gd.client_id,
            initialPlatformId:   gd.platform_id,
            initialCampaignName: gd.name,
            initialGroupId:      campaignId,
            initialGridRows:     rowsByLevel,
          },
        });
      })
      .catch(() => { if (alive) setCampaignCache({ id: campaignId, error: true }); })
      .finally(() => { if (alive) setCampLoading(false); });
    return () => { alive = false; };
  }, [route.view, route.campaignId, clientId, workspaceLoading]);

  if (loading) return (
    <div className="o-state" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      Loading…
    </div>
  );

  if (error || !me?.access) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: T.danger }}>{error ? 'Error' : 'Access Denied'}</div>
        <div style={{ color: T.muted, fontSize: 13, maxWidth: 360, textAlign: 'center' }}>
          {error || "You don't have access to the Taxonomy Generator module."}
        </div>
        <button
          className="o-btn o-btn-primary"
          style={{ marginTop: 8 }}
          onClick={() => { history.pushState(null, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
        >
          Back to Hub
        </button>
      </div>
    );
  }

  const isAdmin           = me?.role?.can_manage_admin;
  const canManageTaxonomy = me?.role?.can_manage_taxonomy || me?.role?.can_manage_admin;

  function renderPage() {
    const { view, settingsTab, campaignId, level, versionId } = route;

    if (['campaign-detail', 'campaign-version', 'builder-new'].includes(view)
        && (workspaceLoading || previousClient.current !== clientId)) {
      return <p className="o-state">Loading workspace...</p>;
    }

    // Settings
    if (view === 'settings' && isAdmin) {
      // An old /settings/countries link lands on Platforms rather than an empty panel: the
      // Countries tab is gone, and a bookmark to it must still open something.
      const tab = SETTINGS_TABS.some(t => t.id === settingsTab) ? settingsTab : 'platforms';
      return (
        <div>
          <div className="o-toggle" style={{ marginBottom: 24 }}>
            {SETTINGS_TABS.map(t => (
              <button key={t.id} aria-selected={tab === t.id}
                onClick={() => navigate(`${BASE}/settings/${t.id}`)}>{t.label}</button>
            ))}
          </div>
          {tab === 'platforms' && <PlatformsPage />}
          {tab === 'clients'   && <ClientsPage />}
          {tab === 'users'     && <TeamAccessPage />}
        </div>
      );
    }

    // Version preview (read-only snapshot view)
    if (view === 'campaign-version') {
      if (campLoading || campaignCache?.id !== campaignId) return <p className="o-state">Loading campaign...</p>;
      if (campaignCache?.error) return <p className="o-state o-error">Campaign not found in an available workspace.</p>;
      return (
        <VersionPreviewPage
          campaignId={campaignId}
          versionId={versionId}
          onBack={() => navigate(`${BASE}/campaigns`)}
          onRestored={() => { setCampaignCache(null); navigate(`${BASE}/campaigns/${campaignId}`); }}
        />
      );
    }

    // View / edit existing campaign
    if (view === 'campaign-detail') {
      if (campLoading || campaignCache?.id !== campaignId) {
        return <div style={{ padding: '60px 0', textAlign: 'center', color: T.muted, fontSize: 13 }}>Loading campaign…</div>;
      }
      if (campaignCache?.error) {
        return (
          <div style={{ padding: '60px 0', textAlign: 'center', color: T.danger, fontSize: 13 }}>
            Campaign not found.{' '}
            <button onClick={() => navigate(`${BASE}/campaigns`)} style={{ background: 'none', border: 'none', color: T.teal, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}>
              ← Back to Campaigns
            </button>
          </div>
        );
      }
      return (
        <BuilderPage
          key={`${clientId}:${campaignId}`}
          me={me}
          targetLevel={level}
          targetUtm={!!route.utm}
          onLevelChange={slug => history.replaceState(null, '', `${BASE}/campaigns/${campaignId}/${slug}`)}
          onGroupSaved={() => {}}
          onBack={() => navigate(`${BASE}/campaigns`)}
          readOnly={!canManageTaxonomy}
          {...campaignCache.props}
        />
      );
    }

    // New campaign / duplicate
    if (view === 'builder-new') {
      return (
        <BuilderPage
          key={clientId}
          me={me}
          targetUtm={!!route.utm}
          onGroupSaved={(gid, suffix = 'campaign') => history.replaceState(null, '', `${BASE}/campaigns/${gid}/${suffix}`)}
          onBack={() => { builderPropsRef.current = {}; navigate(`${BASE}/campaigns`); }}
          {...builderPropsRef.current}
        />
      );
    }

    switch (view) {
      case 'campaigns': return (
        <CampaignsPage me={me}
          onCreateCampaign={() => navigate(`${BASE}/campaigns/new`)}
          onViewCampaign={p => navigate(`${BASE}/campaigns/${p.initialGroupId}`)}
          onDuplicateCampaign={p => { builderPropsRef.current = p; navigate(`${BASE}/campaigns/new`); }}
          onViewVersion={(group, version) => navigate(`${BASE}/campaigns/${group.id}/version/${version.id}`)}
        />
      );
      case 'activity':   return <ActivityPage me={me} />;
      case 'taxonomy':   return canManageTaxonomy ? <TaxonomySetup /> : null;
      case 'utm': return canManageTaxonomy ? <UtmSetup /> : null;
      case 'dictionary': return canManageTaxonomy ? <DataDictionaryPage /> : null;
      default:           return <CampaignsPage me={me} />;
    }
  }

  const activeNav = (() => {
    const { view } = route;
    if (view === 'campaign-detail' || view === 'builder-new' || view === 'campaign-version') return 'campaigns';
    return view;
  })();

  const navTabs = [
    ...MAIN_NAV,
    ...(canManageTaxonomy ? TAXONOMY_NAV : []),
    ...(isAdmin ? SUPER_ADMIN_NAV : []),
  ];

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text }}>
      <style>{`
        ::-webkit-scrollbar{width:8px;height:8px}
        ::-webkit-scrollbar-track{background:${T.bg}}
        ::-webkit-scrollbar-thumb{background:${T.border};border-radius:${4}px}
        ::-webkit-scrollbar-thumb:hover{background:${T.muted}}
        ::-webkit-scrollbar-corner{background:${T.bg}}
        *{scrollbar-width:thin;scrollbar-color:${T.border} ${T.bg}}
      `}</style>

      <OrbitModuleHeader
        title="Taxonomy Generator"
        moduleId="taxonomy-generator"
        tabs={navTabs}
        activeTab={activeNav}
        onTabChange={id => {
          const t = navTabs.find(n => n.id === id);
          if (t) navigate(`${BASE}/${t.path}`);
        }}
      />

      {/* Page content */}
      <main className="o-page">
        {renderPage()}
      </main>
    </div>
  );
}
